// ModalProducto.js
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Box,
  Typography,
  Chip,
  MenuItem,
  Select,
  InputLabel,
  FormControl
} from "@mui/material";

import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";

import { apiFetch } from "../utils/api"; 

const VisuallyHiddenInput = (props) => (
  <input
    {...props}
    style={{
      clip: "rect(0 0 0 0)",
      clipPath: "inset(50%)",
      height: 1,
      overflow: "hidden",
      position: "absolute",
      bottom: 0,
      left: 0,
      whiteSpace: "nowrap",
      width: 1
    }}
  />
);

export default function ModalProducto({
  open,
  onClose,
  editMode,
  newProducto,
  setNewProducto,
  errors,
  almacenes,
  personales,
  proveedores,
  handlePreview,
  handleSaveProducto,
  tipo = "producto",
  saving = false
}) {
  const safeErrors = errors || {};

  // =============================
  // FACTURAS / CONCEPTOS XML
  // =============================
  const [facturas, setFacturas] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [facturaId, setFacturaId] = useState("");
  const [xmlConceptoId, setXmlConceptoId] = useState("");

  // =============================
  // SERIES MÚLTIPLES (solo crear)
  // =============================
  const [serieInput, setSerieInput] = useState("");
  const [seriesList, setSeriesList] = useState([]);
  const [seriesErrors, setSeriesErrors] = useState([]);
  const [botonBloqueado, setBotonBloqueado] = useState(false);

  // Cache de series existentes (para no pedir /api/productos todo el tiempo)
  const [seriesExistentes, setSeriesExistentes] = useState(null);

  // =============================
  // Helpers
  // =============================
  const shortUuid = (u) => (u ? `${u.slice(0, 8)}…${u.slice(-4)}` : "");

  const displayFactura = (f) => {
    const ref = (f?.referencia || "").trim();
    if (ref) return ref;

    const u = (f?.uuid || "").trim();
    if (u) return `UUID ${shortUuid(u)}`;

    return `Factura #${f?.id_factura ?? f?.id ?? "—"}`;
  };

  // Normaliza respuestas que pueden venir como [] o {facturas: []} o {data: []}
  const toArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.facturas)) return data.facturas;
    if (Array.isArray(data?.conceptos)) return data.conceptos;
    if (Array.isArray(data?.rows)) return data.rows;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  };

  // =============================
  // Si abrimos en modo edición, limpiar series internas
  // =============================
  useEffect(() => {
    if (editMode) {
      setSeriesList([]);
      setSeriesErrors([]);
      setBotonBloqueado(false);
    }
  }, [editMode, open]);

  // =============================
  // Sincronizar seriesList hacia el parent (newProducto)
  // =============================
  useEffect(() => {
    if (editMode) return;

    if (seriesList.length === 0) {
      setNewProducto(prev => ({
        ...prev,
        numero_serie: "",
        seriesMultiples: undefined
      }));
    } else if (seriesList.length === 1) {
      setNewProducto(prev => ({
        ...prev,
        numero_serie: seriesList[0],
        seriesMultiples: undefined
      }));
    } else {
      setNewProducto(prev => ({
        ...prev,
        numero_serie: "",
        seriesMultiples: seriesList
      }));
    }

    validarSeries(seriesList);
     
  }, [seriesList]);

  // =============================
  // Cargar facturas al abrir
  // =============================
  useEffect(() => {
    if (!open) return;

    // ✅ si vienes desde factura, ya trae valores el newProducto
    const fId = newProducto?.factura_id ?? "";
    const cId = newProducto?.xml_concepto_id ?? "";

    setFacturaId(fId);
    setXmlConceptoId(cId);
    setConceptos([]);

    const cargarFacturas = async () => {
      try {
        const data = await apiFetch(`/api/facturas/disponibles?tipo=${tipo}`, {
          showError: false
        });
        setFacturas(toArray(data));
      } catch (e) {
        console.error("cargarFacturas error:", e);
        Swal.fire("Aviso", "No se pudieron cargar las facturas disponibles.", "info");
        setFacturas([]);
      }
    };

    const cargarSeriesExistentes = async () => {
      if (editMode) return;
      try {
        const prods = await apiFetch("/api/productos", { showError: false });
        const set = new Set(
          (prods || [])
            .map(p => String(p.numero_serie || "").toUpperCase().trim())
            .filter(Boolean)
        );
        setSeriesExistentes(set);
      } catch (e) {
        console.error("cargarSeriesExistentes error:", e);
        setSeriesExistentes(null);
      }
    };

    cargarFacturas();
    cargarSeriesExistentes();
     
  }, [open, tipo]);

  // =============================
  // Cargar conceptos cuando cambia factura
  // =============================
  useEffect(() => {
    if (!open) return;

    if (!facturaId) {
      setConceptos([]);
      return;
    }

    const cargarConceptos = async () => {
      try {
        const data = await apiFetch(
          `/api/facturas/${facturaId}/conceptos-disponibles?tipo=${tipo}`,
          { showError: false }
        );
        const arr = toArray(data);
        setConceptos(arr);

        // si traías concepto seleccionado pero ya no está disponible, límpialo
        if (xmlConceptoId && !arr.some(c => c.id === xmlConceptoId)) {
          setXmlConceptoId("");
          setNewProducto(prev => ({ ...prev, xml_concepto_id: null }));
        }
      } catch (e) {
        console.error("cargarConceptos error:", e);
        Swal.fire("Aviso", "No se pudieron cargar los conceptos XML.", "info");
        setConceptos([]);
      }
    };

    cargarConceptos();
     
  }, [open, facturaId, tipo]);

  // ============================================================
  // VALIDAR SERIES (internos + BD)
  // ============================================================
  const validarSeries = async (lista) => {
    const limpios = (lista || []).map(v => v.trim()).filter(v => v !== "");
    const errs = Array((lista || []).length).fill("");
    let hayErrores = false;

    // duplicados internos
    limpios.forEach((v, i) => {
      if (limpios.indexOf(v) !== i) {
        errs[i] = "Duplicado.";
        hayErrores = true;
      }
    });

    // duplicados en BD (si tenemos cache)
    if (seriesExistentes instanceof Set) {
      limpios.forEach((v, i) => {
        if (seriesExistentes.has(v.toUpperCase())) {
          errs[i] = "Ya existe en otro activo.";
          hayErrores = true;
        }
      });
    }

    setSeriesErrors(errs);
    setBotonBloqueado(hayErrores);
  };

  // ============================================================
  // AGREGAR SERIE
  // ============================================================
  const agregarSerie = () => {
    const valor = serieInput.trim();
    if (valor === "") return;

    if (seriesList.some(s => s.toUpperCase() === valor.toUpperCase())) {
      Swal.fire("Aviso", "Ya añadiste esa serie.", "warning");
      setSerieInput("");
      return;
    }

    setSeriesList(prev => [...prev, valor]);
    setSerieInput("");
  };

  const eliminarSerie = (index) => {
    setSeriesList(prev => prev.filter((_, i) => i !== index));
  };

  // ============================================================
  // RENDER
  // ============================================================
  if (!newProducto) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ backgroundColor: "primary.main", color: "white" }}>
        {editMode ? "Editar Activo" : "Nuevo Activo"}
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 3 }}>
        {/* FACTURA */}
        <FormControl fullWidth margin="dense">
          <InputLabel>Factura</InputLabel>
          <Select
            value={facturaId}
            onChange={(e) => {
              const id = e.target.value;
              setFacturaId(id);
              setXmlConceptoId("");
              setConceptos([]);
              setNewProducto(prev => ({ ...prev, factura_id: id, xml_concepto_id: null }));
            }}
            label="Factura"
          >
            {facturas.map((f) => (
              <MenuItem key={f.id_factura ?? f.id} value={f.id_factura ?? f.id}>
                {displayFactura(f)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* CONCEPTO XML */}
        <FormControl fullWidth margin="dense" disabled={!facturaId}>
          <InputLabel>Concepto XML</InputLabel>
          <Select
            value={xmlConceptoId}
            onChange={(e) => {
              const id = e.target.value;
              setXmlConceptoId(id);

              const concepto = conceptos.find(c => c.id === id);
              if (concepto) {
                setNewProducto(prev => ({
                  ...prev,
                  descripcion: concepto.descripcion,
                  xml_concepto_id: id
                }));
              } else {
                setNewProducto(prev => ({ ...prev, xml_concepto_id: id }));
              }
            }}
            label="Concepto XML"
          >
            {conceptos.map(c => (
              <MenuItem key={c.id} value={c.id}>
                {c.descripcion} (Disp: {c.disponibles})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* DESCRIPCIÓN */}
        <TextField
          margin="dense"
          label="Descripción*"
          fullWidth
          value={newProducto.descripcion || ""}
          onChange={(e) => setNewProducto({ ...newProducto, descripcion: e.target.value })}
          error={!!safeErrors.descripcion}
          helperText={safeErrors.descripcion}
        />

        {/* MODELO + MARCA */}
        <Box display="flex" gap={2} sx={{ mt: 2 }}>
          <TextField
            margin="dense"
            label="Modelo*"
            fullWidth
            value={newProducto.modelo}
            onChange={(e) => setNewProducto({ ...newProducto, modelo: e.target.value })}
            error={!!safeErrors.modelo}
            helperText={safeErrors.modelo}
          />

          <TextField
            margin="dense"
            label="Marca*"
            fullWidth
            value={newProducto.marca}
            onChange={(e) => setNewProducto({ ...newProducto, marca: e.target.value })}
            error={!!safeErrors.marca}
            helperText={safeErrors.marca}
          />
        </Box>

        {/* NUMERO DE SERIE(s) */}
        <Box sx={{ mt: 3 }}>
          {editMode ? (
            <TextField
              margin="dense"
              label="Número de Serie*"
              fullWidth
              value={newProducto.numero_serie}
              onChange={(e) =>
                setNewProducto({ ...newProducto, numero_serie: e.target.value })
              }
            />
          ) : (
            <>
              <Typography sx={{ mb: 1 }}>Números de serie</Typography>

              <Box display="flex" gap={1}>
                <TextField
                  label="Agregar número de serie"
                  fullWidth
                  value={serieInput}
                  onChange={(e) => setSerieInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      agregarSerie();
                    }
                  }}
                />

                <Button variant="contained" color="success" onClick={agregarSerie}>
                  <FontAwesomeIcon icon={faPlus} />
                </Button>
              </Box>

              <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                {seriesList.map((s, i) => (
                  <Chip
                    key={`serie-${i}-${s}`}
                    label={s}
                    color={seriesErrors[i] ? "error" : "primary"}
                    onDelete={() => eliminarSerie(i)}
                  />
                ))}
              </Box>

              {seriesErrors.some(err => err !== "") && (
                <Box sx={{ mt: 1 }}>
                  {seriesErrors.map((err, i) =>
                    err ? (
                      <Typography key={i} color="error" variant="caption" display="block">
                        Serie {i + 1}: {err}
                      </Typography>
                    ) : null
                  )}
                </Box>
              )}
            </>
          )}
        </Box>

        {/* ORDEN */}
        <TextField
          margin="dense"
          label="Orden de Pedido"
          fullWidth
          sx={{ mt: 2 }}
          value={newProducto.pedido_orden}
          onChange={(e) => setNewProducto({ ...newProducto, pedido_orden: e.target.value })}
        />

        {/* FECHAS */}
        <Box display="flex" gap={2} sx={{ mt: 2 }}>
          <TextField
            margin="dense"
            label="Fecha Compra*"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={newProducto.fecha_compra}
            onChange={(e) => setNewProducto({ ...newProducto, fecha_compra: e.target.value })}
          />

          <TextField
            margin="dense"
            label="Próximo Mantenimiento*"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={newProducto.fecha_mantenimiento}
            onChange={(e) => setNewProducto({ ...newProducto, fecha_mantenimiento: e.target.value })}
          />
        </Box>

        {/* TIPO */}
        <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
          <InputLabel>Tipo*</InputLabel>
          <Select
            value={newProducto.propio_rentado ?? ""}
            onChange={(e) =>
              setNewProducto({ ...newProducto, propio_rentado: e.target.value })
            }
            label="Tipo*"
          >
            <MenuItem value="Propio">Propio</MenuItem>
            <MenuItem value="Rentado">Rentado</MenuItem>
          </Select>
        </FormControl>

        {/* ALMACEN + PERSONAL + PROVEEDOR */}
        <Box display="flex" gap={2} sx={{ mt: 2 }}>
          <FormControl fullWidth margin="dense">
            <InputLabel>Almacén*</InputLabel>
            <Select
              value={newProducto.id_almacen ?? ""}
              onChange={(e) =>
                setNewProducto({ ...newProducto, id_almacen: e.target.value })
              }
            >
              {almacenes
                ?.filter(a => Number(a.id_almacen ?? a.id) > 0)
                .map(a => (
                  <MenuItem
                    key={`almacen-${a.id_almacen ?? a.id}`}
                    value={a.id_almacen ?? a.id}
                  >
                    {a.nombre_almacen}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense">
            <InputLabel>Responsable*</InputLabel>
            <Select
              value={newProducto.id_personal ?? ""}
              onChange={(e) =>
                setNewProducto({ ...newProducto, id_personal: e.target.value })
              }
            >
              {personales
                ?.filter(p => Number(p.id_personal ?? p.id) > 0)
                .map(p => (
                  <MenuItem
                    key={`personal-${p.id_personal ?? p.id}`}
                    value={p.id_personal ?? p.id}
                  >
                    {p.nombre} — {p.puesto}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense">
            <InputLabel>Proveedor*</InputLabel>
            <Select
              value={newProducto.id_proveedor ?? ""}
              onChange={(e) =>
                setNewProducto({ ...newProducto, id_proveedor: e.target.value })
              }
            >
              {proveedores
                ?.filter(p => Number(p.id_proveedor ?? p.id) > 0)
                .map(p => (
                  <MenuItem
                    key={`proveedor-${p.id_proveedor ?? p.id}`}
                    value={p.id_proveedor ?? p.id}
                  >
                    {p.razon_social}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="secondary" disabled={saving}>
          Cancelar
        </Button>

        <Button
          variant="contained"
          color="primary"
          disabled={botonBloqueado || saving}
          onClick={handleSaveProducto}
        >
          {saving ? "Guardando..." : (editMode ? "Actualizar" : "Crear")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
